import { z } from 'zod';
import {
  APP_LINK_DEFAULT_PLATFORM,
  APP_LINK_DEVICE_ID_PATTERN,
  APP_LINK_PLATFORMS,
} from '../constants/appLink.constant';
import { AUTH_SURFACES, OTP_CHANNELS, OTP_CODE_LENGTH, OTP_ORIGINS } from '../constants/identity.constant';
import { UI_MODES } from '../constants/setting.constant';
import { identifierSchema, phoneSchema } from './identity.schema';
import { officerIdentitySchema } from './officer.schema';

/**
 * The authentication contract (PA_REQUIREMENTS §5.8).
 *
 * There are no cookies anywhere in this system: both tokens travel in the response body
 * and the client stores them. That is a deliberate departure from CLAUDE.md §8, taken
 * with the trade-off understood — see CHANGELOG P1a. The compensating control is that
 * session state lives in Redis, so a token can actually be revoked.
 */

export const requestOtpSchema = z
  .object({
    /** ONE field. Not a toggle, not a type discriminator (FR-5.8.4). */
    identifier: identifierSchema,
    surface: z.enum(AUTH_SURFACES),
    /**
     * ⚠️ [P6c §4] COSMETIC, AND THE FIELD DIRECTLY ABOVE IS NOT.
     *
     * It selects ONE WORD in the SMS body — the `{application}` slot of a registered DLT
     * template — and nothing else. `surface` is frozen onto the OTP row and decides the token
     * audience; this is a label. `identity.constant.ts` states the contrast in full, and the
     * two tuples deliberately share no member so passing one where the other belongs is a
     * compile error.
     *
     * ⚠️ THERE IS NO CROSS-CHECK BETWEEN THE TWO, AND THERE MUST NOT BE. A refinement
     * rejecting `surface: 'officer'` with `origin: 'admin'` would make `origin` constrain an
     * authorisation input, which is precisely how a cosmetic field becomes a security one.
     * The mismatched pair is VALID, mints an officer token, and is asserted as such.
     *
     * Defaults to `app`, so a caller that says nothing gets the officer app's branding — the
     * volume case, and the one with no operator standing by to correct a mistake.
     */
    origin: z.enum(OTP_ORIGINS).default('app'),
  })
  .superRefine((value, ctx) => {
    /**
     * OD-23 — the officer surface accepts phone only.
     *
     * This is a FORMAT rule, checked before any lookup, so it reveals nothing about
     * whether an account exists. Enforcing it here rather than in the resolver keeps
     * the resolver itself surface-agnostic (FR-5.8.4's "one code path, never two").
     */
    if (value.surface === 'officer' && value.identifier.includes('@')) {
      ctx.addIssue({ code: 'custom', path: ['identifier'], message: 'Enter your 10-digit mobile number.' });
    }
  });

const fcmToken = z.string().trim().min(1).max(4096).optional().catch(undefined);

export const verifyOtpSchema = z.object({
  /**
   * 16 random bytes, base64url. NOT the document's ObjectId: an ObjectId's counter is
   * sequential per process, so an attacker who makes one request can guess neighbouring
   * request ids — and against a 10^6 code space that is a targeted brute-force channel.
   */
  requestId: z.string().regex(/^[A-Za-z0-9_-]{22}$/, 'Invalid request'),
  code: z.string().regex(new RegExp(`^\\d{${OTP_CODE_LENGTH}}$`), `Enter the ${OTP_CODE_LENGTH}-digit code`),
  /**
   * ⚠️ [C1h §6e] WHERE THE DEVICE ALREADY HELD LOCATION PERMISSION — AND NOWHERE ELSE.
   *
   * The officer app NEVER PROMPTS AT LOGIN. [P5c] §4d established that a police app demanding
   * location before showing anything reads as surveillance, and asking at the door is worse
   * than asking at a marking; the client queries `navigator.permissions` and sends this only
   * on `state === 'granted'`. No permission ⇒ absent, and that is the ordinary case.
   *
   * ⚠️ `.optional()` RATHER THAN REQUIRED-NULLABLE, WHICH IS [P6d]'s TEST APPLIED. A required
   * key carrying `null` is a change to EVERY caller — the dashboard login, the officer app,
   * six e2e fixtures and the Postman collection — and no caller who will never send it has to
   * render anything. `undefined` is omitted by `JSON.stringify`, so no existing request moves
   * a byte.
   *
   * ⚠️ AND IT REACHES NO AUTHORISATION DECISION, WHICH IS WHY THE PARAGRAPH BELOW DOES NOT
   * FORBID IT. `surface` is absent because supplying it again would be an escalation; `origin`
   * because nothing at verify has a use for it. This is neither: it is written to a telemetry
   * row AFTER the session is issued and is read by nothing on the auth path. It is also, by
   * construction, a value the client controls — which is why it records where the DEVICE said
   * it was and is never compared with the IP.
   */
  coords: z
    .object({
      lat: z.number().min(-90).max(90),
      lng: z.number().min(-180).max(180),
    })
    .optional(),
  fcm_token: fcmToken,
  platform: z.enum(['web', 'android', 'ios', 'admin']).optional().catch(undefined),
});

/**
 * NOTE WHAT IS ABSENT: `surface`.
 *
 * It is frozen onto the OTP request when the code is sent. If the caller could supply
 * it again here, an officer could request an officer-surface code and redeem it for a
 * dashboard session — which is exactly the privilege escalation that having two token
 * audiences exists to prevent.
 *
 * ⚠️ [amended P6c] `origin` IS ABSENT TOO, FOR THE OPPOSITE REASON — not because supplying
 * it again would be dangerous, but because nothing at verify time has any use for it. It was
 * spent composing one message and was never stored; there is no column on `otp_requests` to
 * read it back from, which is what makes "it cannot reach an authorisation decision" a
 * property of the schema rather than a promise in a comment.
 */

export const refreshSchema = z.object({
  refreshToken: z.string().min(1, 'A refresh token is required'),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C1b §6a] AN IDEMPOTENCY KEY, SO A ROTATION WHOSE ANSWER WAS LOST DOES NOT COST A
   *  SESSION — AND IT IS OPTIONAL, WHICH IS DOING THREE JOBS AT ONCE.
   *
   *  [C1a] recorded the residual honestly: a rotation whose response never reaches the client
   *  leaves the server ROTATED and the client holding a spent token, so the next attempt is a
   *  FALSE reuse and the whole family dies. On a handset with poor signal that is ordinary
   *  rather than exceptional.
   *
   *  ⚠️ **THIS IS NOT THE GRACE WINDOW §1c FORBIDS.** A grace window forgives ANY presentation
   *  of a rotated token for N milliseconds. This forgives only a presentation carrying the
   *  exact 128 bits the server itself wrote against that one rotation. A real replay carries a
   *  different key, or none, and the family dies for it exactly as before.
   *
   *  ⚠️ **OPTIONAL DOES THREE JOBS.** It keeps all 23 of `authTokens.test.ts`'s claims passing
   *  UNEDITED — their bodies carry `refreshToken` alone, so `presentedKey` is `undefined` and
   *  the executed path is literally today's; a cached older bundle behaves exactly as it did;
   *  and because `z.object` STRIPS unknown keys, a newer client against an older server
   *  degrades silently rather than 422-ing.
   *
   *  ⚠️ **THE SHAPE IS `clientEventId`'s AND THE ENTROPY ARGUMENT IS `requestId`'s.** A
   *  client-minted opaque id, charset-restricted and length-BANDED rather than fixed so the
   *  encoding is not frozen into the server; 16 bytes of CSPRNG, because a guessable key is a
   *  bypass of the immediate detection. The 64-character ceiling is what stops an unbounded
   *  string reaching a Redis hash field.
   *
   *  ⚠️ A MALFORMED KEY IS A 422 FROM `validate` BEFORE ANY HANDLER RUNS, so it does NOT
   *  revoke the family. That is not a hole: it buys an attacker nothing but a second attempt
   *  with a well-formed key, which does.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  rotationKey: z
    .string()
    .regex(/^[A-Za-z0-9_-]{22,64}$/, 'Invalid rotation key')
    .optional(),
});

export const logoutSchema = z.object({
  refreshToken: z.string().min(1).optional(),
  /** Ends every session for this officer on every surface, not just this one. */
  allSessions: z.boolean().default(false),
});

export const releaseFcmTokenSchema = z.object({
  fcm_token: fcmToken,
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [APP-LINK] `POST /auth/request-app-link` — PUBLIC, AND IT ANSWERS EVERY NUMBER ALIKE.
 *
 *  `phone` is `phoneSchema`, a TRANSFORM: what reaches `res.locals.validated` is already the
 *  canonical ten digits, so `+91 98765 43210`, `09876543210` and `9876543210` are one number to
 *  every meter and to the lookup — reformatting a number cannot buy it a fresh daily allowance.
 *
 *  `platform` defaults to Android, `requestOtpSchema.origin`'s defaulted-enum shape. `deviceId`
 *  keys the per-device meter alone and is optional: a caller that sends none is counted by its
 *  network address instead. A malformed one is a 422, `rotationKey`'s precedent — the page never
 *  sends one, and a refusal that depends only on the request's own shape discloses nothing.
 *
 *  ⚠️ UNKNOWN KEYS ARE STRIPPED (§6). There is no `surface` and no `origin`: nothing here mints a
 *  token, and the SMS body has no slot a caller could choose a word for.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const requestAppLinkSchema = z.object({
  phone: phoneSchema,
  platform: z.enum(APP_LINK_PLATFORMS).default(APP_LINK_DEFAULT_PLATFORM),
  deviceId: z.string().regex(APP_LINK_DEVICE_ID_PATTERN, 'Invalid device id').optional(),
});

/** The 202 from `request-otp`. Identical whether or not the account exists (FR-5.8.18). */
export const otpChallengeSchema = z.object({
  requestId: z.string(),
  expiresAt: z.iso.datetime(),
  /** When a resend becomes possible. A cooldown no admin role can clear (FR-5.8.17). */
  resendAfter: z.iso.datetime(),
  codeLength: z.number().int(),
});

export const sessionSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** Seconds, so a client does not have to reason about clock skew to schedule a refresh. */
  expiresIn: z.number().int(),
  refreshExpiresIn: z.number().int(),
  officer: officerIdentitySchema,
});

/**
 * Public and unauthenticated, because the login screen needs the OTP shape before anyone
 * has a token.
 *
 * ⚠️ [amended P5c] `otpBypassEnabled` WAS HERE AND HAS BEEN REMOVED, AND THE OLD REASONING
 * NO LONGER HOLDS.
 *
 * It read: "Yes, this tells an anonymous caller that bypass is on. That is correct: bypass
 * is by definition a mode in which `000000` works for everybody, so it is not a secret."
 * That was true while the code WAS `000000` and sat in the repository — nothing was given
 * away. P5c made the code per-deployment configuration with no default, so the mode is now
 * a hint it never used to be: "there is a fixed six-digit code that works for every account
 * here" is exactly the sentence you would not volunteer to an anonymous caller.
 *
 * And its only consumer was the banner, which §11 removed. A field with no reader is §9's
 * dead surface; a field with no reader that narrows a secret is worse than that.
 *
 * `codeLength` stays: the OTP input renders that many boxes, and it discloses nothing.
 */
export const authConfigSchema = z.object({
  codeLength: z.number().int(),
  otpTtlSeconds: z.number().int(),
  resendCooldownSeconds: z.number().int(),
  channels: z.array(z.enum(OTP_CHANNELS)),
  /**
   * ⚠️ [C1h §1e] THE SYSTEM-WIDE UI MODE, DELIVERED HERE BECAUSE THE LOGIN SCREEN IS PAINTED
   * IN IT.
   *
   * This endpoint was the right carrier for three reasons and it had ZERO production
   * consumers before this pass: it is PUBLIC and pre-authentication, so a value on it is
   * readable before anybody has a token; it is already parsed through this schema on the way
   * out; and `ThemeProvider` sits ABOVE `QueryClientProvider` and `AuthProvider` in
   * `App.tsx`, so the theme layer structurally cannot await an authenticated read.
   *
   * ⚠️ IT IS A REVALIDATION, NEVER THE FIRST PAINT. The client paints from a localStorage
   * cache in the pre-paint script and writes this answer back for the NEXT load — §1e's
   * "do not block first paint on a network call". A palette applied when this response lands
   * would repaint in front of the reader, which is the flash the whole design avoids.
   *
   * ⚠️ AND IT DISCLOSES NOTHING. Which of two palettes a deployment renders is visible to any
   * anonymous caller who loads the login page at all.
   */
  uiMode: z.enum(UI_MODES),
});
